/**
 * Endpoints du domaine payroll (gestion de paie) — source canonique.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 *
 * - Phase 9 (guide v1.2) : la paie était un type de transaction du
 *   wallet-service (`PAYROLL_CREDIT`), sans service dédié. Les appels
 *   passaient par `endpoints.wallet.transactions.*` et
 *   `endpoints.feeEngine.feeQuotes.*`. Aucun endpoint propre.
 * - Phase 22 (guide v4.1 §6.11) : nouveau microservice `payroll-service`
 *   (préfixe `/gw/payroll/api/v1/`). Ressource `PayrollBatches` — paie de
 *   masse marchand, 17 opérations exposées à `flashpay-merchant` et BO.
 *   Chemins copiés tels quels de
 *   `docs/guides/v4.1/FlashPay.postman_collection.json` (dossier
 *   `20 - Paie de masse (payroll-service)`).
 */
export const payrollEndpoints = {
  payrollBatches: {
    /** GET — modèle Excel (.xlsx) à remplir. Rôle `flashpay-merchant`. */
    template: "/gw/payroll/api/v1/PayrollBatches/template",
    /** POST — crée une paie en préparation à partir d'un corps JSON. */
    add: "/gw/payroll/api/v1/PayrollBatches/add",
    /** POST — liste paginée des paies (filtre status / période). */
    all: "/gw/payroll/api/v1/PayrollBatches/all",
    /** POST — import multipart (file xlsx + label + feePayer) → paie DRAFT. */
    import: "/gw/payroll/api/v1/PayrollBatches/import",
    /** GET — tableau de bord (nombre de paies, salariés payés, masse, frais). */
    stats: "/gw/payroll/api/v1/PayrollBatches/stats",
    /** GET — détail d'une paie et de ses lignes. */
    detail: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}`,
    /** PUT — modifie label / feePayer sur paie DRAFT ou SIMULATED (revient en DRAFT). */
    update: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}`,
    /** POST — annule une paie DRAFT ou SIMULATED (CANCELLED). */
    cancel: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/cancel`,
    /**
     * POST — lance une paie SIMULATED. **Idempotence obligatoire**
     * (`Idempotency-Key`, §4.7). Rejouer avec la même clé ne débite
     * jamais deux fois.
     */
    launch: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/launch`,
    /** POST — relit l'état d'une paie PROCESSING auprès de wallet-service. */
    refresh: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/refresh`,
    /** POST — simule une paie DRAFT (sans mouvement d'argent). */
    simulate: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/simulate`,
    /** POST — ajoute un salarié à une paie DRAFT ou SIMULATED. */
    addLine: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/lines`,
    /** PUT — corrige une ligne (nom, téléphone, montant, matricule). */
    updateLine: (payrollBatchId: string, lineId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/lines/${lineId}`,
    /** DELETE — retire un salarié d'une paie DRAFT ou SIMULATED. */
    deleteLine: (payrollBatchId: string, lineId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/lines/${lineId}`,
    /** GET — relevé PDF individuel d'un salarié payé. */
    lineStatement: (payrollBatchId: string, lineId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/lines/${lineId}/statement`,
    /** GET — relevé PDF global d'une paie lancée (synthèse + détail). */
    statement: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/statement`,
    /** GET — archive ZIP contenant tous les relevés individuels des payés. */
    statements: (payrollBatchId: string) =>
      `/gw/payroll/api/v1/PayrollBatches/${payrollBatchId}/statements`,
  },
} as const;
